/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {signal} from '@preact/signals-react';
import {createMutator} from '@ion/applications/src/store/SignalUtils';
import {Profile, Vehicle} from '@ion/core/src/types/entities';
import {getViewSubGroupsState, gridDefaultPageSize} from '../../../../utils/helpers';
import {defaultGridSettings, updateGridState} from '../../store-helpers';
import {
  Context,
  GridState,
  PID,
  ProfileLookup,
  ProfileMode
} from '../../types';

export const PREFERENCE = 'profile-manager';

export const isLoading          = signal(false);
export const isInitialized      = signal(false);
export const selectedTab        = signal(Context.test);
export const columnState        = signal(null);
export const columnPreferences  = signal(null);
export const hasUpdate          = signal(false);
export const profilePageSize    = signal(gridDefaultPageSize);
export const busProfilePageSize = signal(gridDefaultPageSize);

/**
 * Defines a reference to the currently selected Vehicle Profile Tab.
 *
 * ```typescript
 *  import {activeProfileTab} from '../store';
 *  ...
 *
 *  // reference current activeProfileTab value ...
 *  activeProfileTab.value;
 * ```
 *
 * {@link Context}
 */
export const activeProfileTab = signal<Context>(Context.test);

/**
 * Provides the mutation function which is responsible for updating the state of
 * the selected Vehicle Profile Tab.
 *
 * ```typescript
 *  import {
 *    activeProfileTab,
 *    setActiveProfileTab
 *  } from '../store';
 *  ...
 *
 *  // reference current activeProfileTab value ...
 *  activeProfileTab.value; // 'test'
 *  ...
 *
 *  // update activeProfileTab value via mutator function ...
 *  setActiveProfileTab(Context.production);
 * ```
 *
 * {@link activeProfileTab}
 * {@link Context}
 */
export const setActiveProfileTab = (tab: Context) => {
  activeProfileTab.value = tab;
};

/**
 * Signal to manage whether Include SubAccounts are toggled on/off.
 */
export const includeSubGroups = signal<boolean>(getViewSubGroupsState(PREFERENCE));

/**
 * Mutation function to update the `includeSubGroups` state.
 *
 * @param include Boolean indicating whether Sub-Groups should be included.
 */
export const setIncludeSubGroups = (include: boolean): void => {
  includeSubGroups.value = include;
  setGridState({includeSubGroups: include});
};

/**
 * Defines a reference to the user's currently selected profileGrid settings.
 *
 * {@link defaultGridSettings}
 * {@link GridState}
 */
export const profileGrid = signal<Partial<GridState>>({
  ...defaultGridSettings
});

/**
 * Provides the mutation for updating the current state of the Vehicle Profile
 * Grid based on the user's current settings. This mutator function can be used
 * to update the  profileGrid state in a partial manner, or to completely overwrite
 * the current state with the provided state.
 *
 * {@link defaultGridSettings}
 * {@link profileGrid}
 * {@link GridState}
 * @param gridState The current state for which the grid state is to be set, or
 *        when the 'partial' argument is set to `true` (default), only the values
 *        that are updated in the `gridState` will be used to update the current
 *        state.
 * @param partial When `true` (default), the provided state will be merged with
 *        the current state, otherwise, when `false`, the current state will be
 *        overwritten with that of the provided state via the `gridState` arg.
 * @returns The updated state of the grid based on the provided arguments.
 */
export const setGridState = (gridState: Partial<GridState>, partial = true): Partial<GridState> => (
  updateGridState(gridState, partial, profileGrid)
);

/**
 * Provides a mutation function which is responsible for updating the state of
 * isInitialized signal.
 *
 * ```typescript
 * import {setIsInitialized} from '../store';
 *
 * // update isInitialized state ...
 * setIsInitialized(true);
 * ```
 */
export const setIsInitialized = (initialized: boolean) => {
  isInitialized.value = initialized;
};

/**
 * Provides a mutation function which is responsible for updating the state of
 * busProfilePageSize signal.
 *
 * ```typescript
 * import {setBusProfilePageSize} from '../store';
 *
 * // update busProfilePageSize state ...
 * setBusProfilePageSize(50);
 * ```
 */
export const setBusProfilePageSize = (size: number) => {
  busProfilePageSize.value = size;
};

/**
 * Signal to manage the currently selected Profile.
 */
export const selectedProfile = signal<Profile>(null);

/**
 * Mutation function to update the selectedProfile state.
 *
 * @param profile The Profile to set as the selected Profile.
 */
export const setSelectedProfile = (profile: Profile): void => {
  selectedProfile.value = profile;
};

/**
 * Signal to manage the currently selected Vehicle.
 */
export const selectedVehicle = signal<Vehicle>(null);

/**
 * Mutation function to update the selectedVehicle state.
 *
 * @param vehicle The Vehicle to set as the selected Vehicle.
 */
export const setSelectedVehicle = (vehicle: Vehicle): void => {
  selectedVehicle.value = vehicle;
};

/**
 * Signal to manage the currently selected VIN.
 */
export const selectedVin = signal<string>(null);

/**
 * Mutation function to update the selectedVin state.
 *
 * @param vin The VIN to set as the selected VIN.
 */
export const setSelectedVin = (vin: string): void => {
  selectedVin.value = vin;
};

/**
 * Signal to manage the currently selected Parameter.
 */
export const selectedParameter = signal<PID>(null);

/**
 * Mutation function to update the selectedParameter state.
 *
 * @param parameter The Parameter to set as the selected Parameter.
 */
export const setSelectedParameter = (parameter: PID): void => {
  selectedParameter.value = parameter;
};

/**
 * Signal to manage the currently selected Parameters to be promoted to production.
 */
export const selectedParameters = signal<number[]>([]);

/**
 * Mutation function to update the selectedParameters state.
 *
 * @param parameters The Parameters to set as the selected Parameters.
 */
export const setSelectedParameters = (parameters: number[]): void => {
  selectedParameters.value = parameters;
};

/**
 * Signal to manage the currently selected Supported PIDs.
 */
export const selectedSupportedPIDs = signal<string[]>(null);

/**
 * Mutation function to update the selectedSupportedPIDs state.
 *
 * @param pids The Supported PIDs to set as the selected Supported PIDs.
 */
export const setSelectedSupportedPIDs = (pids: string[]): void => {
  selectedSupportedPIDs.value = pids;
};

export const resetSelectedProfileValues = () => {
  setSelectedParameters([]);
  setSelectedParameter(null);
  setSelectedProfile(null);
  setSelectedVehicle(null);
  setSelectedVin(null);
};

/**
 * Signal to manage the add vehicle profile dialog.
 */
export const addVehicleProfile = signal<boolean>(false);

/**
 * Mutation function to update the addVehicleProfile state.
 */
export const setAddVehicleProfile = (profile: boolean): void => {
  addVehicleProfile.value = profile;
};

/**
 * Signal to manage the ymmef dialog.
 */
export const selectedYMMEF = signal<boolean>(false);

/**
 * Mutation function to update the selectedYMMEF state.
 */
export const setSelectedYMMEF = (value: boolean): void => {
  selectedYMMEF.value = value;
};

/**
 * Signal to manage the dialog for adding new PID
 */
export const addNewPID = signal<boolean>(false);

/**
 * Mutation function to update the addNewPID state.
 */
export const setAddNewPID = (pid: boolean): void => {
  addNewPID.value = pid;
};

/**
 * Signal to manage the dialog for editing PID
 */
export const editVehiclePID = signal<boolean>(false);

/**
 * Mutation function to update the existing PID.
 */
export const setEditVehiclePID = (pid: boolean): void => {
  editVehiclePID.value = pid;
};

/**
 * Signal to manage the dialog for editing PID
 */
export const pidRowNumber = signal<Number>();

/**
 * Mutation function to update the existing PID.
 */
export const setPidRowNumber = (row: Number): void => {
  pidRowNumber.value = row;
};

/**
 * Signal to manage selected row in PID's table
 */
export const selectedPidRow = signal(null);

/**
 * Mutation function to capture selected row in PID's table
 */
export const setSelectedPidRow = (profile): void => {
  selectedPidRow.value = profile;
};

/**
 * Signal to manage the current profile view/edit mode.
 */
export const profileMode = signal<ProfileMode | null>(null);

/**
 * Mutation function to update the profileMode state.
 *
 * @param mode The mode to set for the profile.
 */
export const setProfileMode = (mode: ProfileMode | null): void => {
  profileMode.value = mode;
};

/**
 * Optional: signal for showing the profile dialog.
 */
export const showProfileDialog = signal<boolean>(false);

export const setVehicleProfilePageSize = (size) => {
  profilePageSize.value = size;
};

//Column State
export const profileManagerColumnState = signal(null);
export const setProfileManagerColumnState = (columnState) => {
  profileManagerColumnState.value = columnState;
};

export const setProfileManagerHasUpdate = (updated: boolean) => {
  hasUpdate.value = updated;
};

/**
 * Defines the state for updating search queries.
 */
export const profileManagerSearchQueries = signal(null);
export const setProfileManagerSearchQueries = createMutator(profileManagerSearchQueries);

/**
 * Defines the numeric profile identifiers Profile Manager is currently pinned
 * to, set when the user opens the page via a vehicle's `View Associated
 * Profiles` action. While this holds a value, the grid queries the profiles
 * `searchById` API with these identifiers instead of the modular-search text
 * terms, so a single exact profile is returned rather than wildcard matches.
 *
 * ```typescript
 *  import {profileManagerProfileLookup} from '../store';
 *  ...
 *
 *  // reference current profileManagerProfileLookup value ...
 *  profileManagerProfileLookup.value;
 * ```
 *
 * {@link ProfileLookup}
 */
export const profileManagerProfileLookup = signal<ProfileLookup | null>(null);

/**
 * Provides the mutation function which is responsible for updating the state of
 * the Profile Manager profile lookup.
 *
 * ```typescript
 *  import {setProfileManagerProfileLookup} from '../store';
 *  ...
 *
 *  // pin Profile Manager to a single profile ...
 *  setProfileManagerProfileLookup({
 *    year   : 44,
 *    make   : 209,
 *    model  : 8286,
 *    engine : 4294967295,
 *    fuel   : 15
 *  });
 * ```
 *
 * {@link profileManagerProfileLookup}
 */
export const setProfileManagerProfileLookup = createMutator(profileManagerProfileLookup);

/**
 * Clears the Profile Manager profile lookup, returning the grid to the
 * standard modular-search behavior.
 *
 * ```typescript
 *  import {clearProfileManagerProfileLookup} from '../store';
 *  ...
 *
 *  // return Profile Manager to a normal search ...
 *  clearProfileManagerProfileLookup();
 * ```
 *
 * {@link profileManagerProfileLookup}
 */
export const clearProfileManagerProfileLookup = (): void => {
  setProfileManagerProfileLookup?.(null);
};
